import React, { useCallback, useEffect, useRef } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import type { LiveTranscriptLine } from "../../../types/liveTranscript";
import { useAudioSyncedTranscript } from "../../../hooks/useAudioSyncedTranscript";
import { LiveTranscriptKaraokeText } from "./live-transcript-karaoke-text";
import {
  contextPanelStyles,
  contextSectionStyles,
  contextMetaRowStyles,
  contextSectionTitleStyles,
  contextSectionBodyStyles,
  contextSectionBodyCompactStyles,
  sessionStatusStyles,
  transcriptPlaceholderStyles,
  transcriptSectionStyles,
  transcriptListStyles,
  transcriptLineStyles,
  transcriptRoleStyles,
  transcriptTextStyles,
  LIVE_SESSION_AI_BLUE,
  LIVE_SESSION_AI_BLUE_LIGHT,
} from "./styles";

export type AvatarSessionStatus =
  | "Connecting"
  | "Listening"
  | "Speaking"
  | "AI speaking";

interface AvatarSessionContextPanelProps {
  objective: string;
  status: AvatarSessionStatus;
  domain?: string | null;
  focusDisplayName?: string | null;
  transcriptLines?: LiveTranscriptLine[];
  isCoachingMode?: boolean;
  doctorName?: string | null;
  isAiSpeaking?: boolean;
}

const NEAR_BOTTOM_PX = 64;

export const AvatarSessionContextPanel: React.FC<
  AvatarSessionContextPanelProps
> = ({
  objective,
  status,
  domain,
  focusDisplayName,
  transcriptLines = [],
  isCoachingMode = false,
  doctorName = null,
  isAiSpeaking = false,
}) => {
  const theme = useTheme();
  const listRef = useRef<HTMLDivElement | null>(null);
  const followRef = useRef(true);
  const karaokeLines = useAudioSyncedTranscript(transcriptLines, isAiSpeaking);
  const aiBlue =
    theme.palette.mode === "dark"
      ? LIVE_SESSION_AI_BLUE
      : LIVE_SESSION_AI_BLUE_LIGHT;
  const statusColor =
    status === "Listening"
      ? "#22C55E"
      : status === "Speaking" || status === "AI speaking"
        ? aiBlue
        : theme.palette.text.secondary;

  const assistantLabel = isCoachingMode
    ? "Coach"
    : doctorName?.trim() || "Doctor";

  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const distanceFromBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight;
    followRef.current = distanceFromBottom <= NEAR_BOTTOM_PX;
  }, []);

  useEffect(() => {
    if (karaokeLines.length === 0) {
      followRef.current = true;
    }
  }, [karaokeLines.length]);

  // Auto-follow only when the turn list grows / completes — not every karaoke tick.
  const followKey = karaokeLines
    .map(
      (line) =>
        `${line.turnId}:${line.text.length}:${line.isComplete ? 1 : 0}:${line.isFinal ? 1 : 0}`,
    )
    .join("|");

  useEffect(() => {
    if (!followRef.current) return;
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [followKey]);

  return (
    <Box sx={contextPanelStyles} component="aside" aria-label="Session context">
      <Box sx={contextSectionStyles}>
        <Typography sx={contextSectionTitleStyles}>Your Objective</Typography>
        <Typography sx={contextSectionBodyCompactStyles}>{objective}</Typography>
      </Box>

      <Box sx={contextMetaRowStyles}>
        {(domain || focusDisplayName) && (
          <Box sx={{ ...contextSectionStyles(theme), flex: "1 1 0", minWidth: 0 }}>
            <Typography sx={contextSectionTitleStyles}>Focus</Typography>
            <Typography sx={contextSectionBodyStyles}>
              {[domain, focusDisplayName].filter(Boolean).join(" · ") || "—"}
            </Typography>
          </Box>
        )}

        <Box sx={{ ...contextSectionStyles(theme), flex: "1 1 0", minWidth: 0 }}>
          <Typography sx={contextSectionTitleStyles}>Session Status</Typography>
          <Typography sx={sessionStatusStyles(statusColor)}>{status}</Typography>
        </Box>
      </Box>

      <Box sx={transcriptSectionStyles}>
        <Typography sx={contextSectionTitleStyles}>Transcript</Typography>
        {karaokeLines.length === 0 ? (
          <Typography sx={transcriptPlaceholderStyles}>
            Conversation appears here as you speak.
          </Typography>
        ) : (
          <Box
            ref={listRef}
            sx={transcriptListStyles}
            onScroll={handleScroll}
            role="log"
            aria-live="polite"
            aria-relevant="additions"
          >
            {karaokeLines.map((line) => {
              const isUser = line.role === "user";
              return (
                <Box key={line.turnId} sx={transcriptLineStyles}>
                  <Typography sx={transcriptRoleStyles(isUser)}>
                    {isUser ? "You" : assistantLabel}
                  </Typography>
                  {isUser ? (
                    <Typography sx={transcriptTextStyles(line.isFinal)}>
                      {line.text}
                    </Typography>
                  ) : (
                    <LiveTranscriptKaraokeText
                      text={line.text}
                      currentTimeSec={line.currentTimeSec}
                      durationSec={line.durationSec}
                      isPlaying={line.isPlaying}
                      isComplete={line.isComplete}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};
